import { useBranchStore } from '@/store/branch'
import { useRegionStore } from '@/store/region'
import { useCurrencyStore } from '@/store/currency'

import { useRegionChecker } from '@/composables/useRegion'
import { DEFAULT_REGION_ID } from '@/constants/regions'
import type { LangEnum } from '@/constants/locales'

export default defineNuxtRouteMiddleware(async (to, from) => {
  const { $i18n, $pinia } = useNuxtApp()
  const regionIdCookie = useRegionIdCookie()
  const { loadLocaleMessages } = useLocaleMessages()

  const branchStore = useBranchStore($pinia)
  const regionStore = useRegionStore($pinia)
  const currencyStore = useCurrencyStore($pinia)

  const { setLocale, locale } = $i18n

  const isBranchesPage = checkIfBranchesPage(to.name)
  const isRegionPage = checkIfRegionPage(to.name)
  const isServerBranchesPage = import.meta.server && isBranchesPage

  const apiRequests = [
    ...(!regionStore.currentRegion?.region_id ? [regionStore.fetchRegionList()] : []),
    ...(!regionStore.regionDetectInfo ? [regionStore.fetchRegionDetect()] : []),
    ...(!currencyStore.currencyList.length ? [currencyStore.fetchCurrency()] : []),
    ...(isServerBranchesPage && to.params.bid ? [branchStore.fetchBranch(to.params.bid as string)] : []),
  ]

  if (apiRequests.length > 0) {
    await Promise.all(apiRequests)
  }

  const { detectValidLang } = useRegionChecker()

  const storeRegionIds = regionStore.regionList.map(region => region.region_id)
  const detectedRegionId = regionStore.regionDetectInfo?.id
  const routeRegionId = to.params.rid ? Number(to.params.rid) : undefined
  const fromRegionId = from.params.rid ? Number(from.params.rid) : undefined
  const isRouteRegionIdValid = storeRegionIds.includes(routeRegionId)
  const isFromRegionIdValid = storeRegionIds.includes(fromRegionId)

  const validRegionId = (() => {
    if (isRouteRegionIdValid) return routeRegionId
    if (isFromRegionIdValid) return fromRegionId

    const tmpResult = regionStore.currentRegionId ?? regionIdCookie.value ?? detectedRegionId ?? DEFAULT_REGION_ID

    if (isServerBranchesPage) {
      const isSupportedRegion = branchStore.saleRegionIds.includes(tmpResult)
      return isSupportedRegion ? tmpResult : branchStore.saleRegionIds[0] || tmpResult
    }

    return tmpResult
  })()

  if (regionStore.currentRegionId !== validRegionId) {
    regionStore.currentRegionId = validRegionId
  }

  const { isCurrentLangValid, supportedLang } = detectValidLang(locale.value as LangEnum)

  if (locale.value !== supportedLang) {
    // Only load on client side; server side handled by plugin
    if (import.meta.client) {
      await loadLocaleMessages(supportedLang)
    }
    await setLocale(supportedLang)
  }

  if (isRegionPage && (!storeRegionIds.length || !isRouteRegionIdValid)) {
    return navigateTo(`/${supportedLang}/regions/${detectedRegionId}`, { replace: true })
  }

  if (!isCurrentLangValid) {
    const path = replaceLangInPath(to.fullPath, supportedLang)
    const routeRaw = { path, query: to.query, hash: to.hash }

    return navigateTo(routeRaw, { replace: true })
  }
})
